/**
 * features/hr/PayrollHub.tsx
 * ------------------------------------------------------------------
 * HR PAYROLL — the Payroll hub. One screen, one hero, the whole
 * monthly pipeline as tabs in the order the bursar works:
 *
 *   1. Workspace        — edit each employee's payroll (stepper)
 *   2. Pay Run          — preview all, generate, email
 *   3. Deduction Reports — per-provider remittance lists (CSV)
 *   4. Bank Export      — net-pay file for the bank + summary
 *
 * Each child renders embedded (no hero of its own).
 * ------------------------------------------------------------------ */
import { useState } from 'react';
import { Banknote, Users, Play, FileSpreadsheet, Landmark, Lock, Scale, Wallet, FileDown, type LucideIcon } from 'lucide-react';
import { GradientHero } from '@/components/ui/glass';
import PayrollWorkspace from '@/features/hr/PayrollWorkspace';
import { BasicPayManager } from '@/features/hr/BasicPay';
import PayRun from '@/features/hr/PayRun';
import BulkPayslips from '@/features/hr/BulkPayslips';
import DeductionReports from '@/features/hr/DeductionReports';
import PayrollBankExport from '@/features/hr/PayrollBankExport';
import PayrollPeriods from '@/features/hr/PayrollPeriods';
import LoanReconciliation from '@/features/hr/LoanReconciliation';

type HubTab = 'workspace' | 'basicpay' | 'payrun' | 'bulk' | 'reports' | 'bank' | 'recon' | 'periods';

const TABS: { id: HubTab; label: string; icon: LucideIcon }[] = [
  { id: 'basicpay', label: 'Basic Pay & Allowances', icon: Wallet },
  { id: 'workspace', label: 'Workspace', icon: Users },
  { id: 'payrun', label: 'Pay Run', icon: Play },
  { id: 'bulk', label: 'Bulk Payslips', icon: FileDown },
  { id: 'reports', label: 'Deduction Reports', icon: FileSpreadsheet },
  { id: 'bank', label: 'Bank Export', icon: Landmark },
  { id: 'recon', label: 'Loan Reconciliation', icon: Scale },
  { id: 'periods', label: 'Periods', icon: Lock },
];

export default function PayrollHub({ onOpenStaff }: { onOpenStaff?: (id: string) => void }) {
  const [tab, setTab] = useState<HubTab>('workspace');

  return (
    <div className="space-y-6">
      <GradientHero
        icon={Banknote}
        title="Payroll"
        subtitle="Prepare · run · report · export"
      />

      <div className="flex flex-wrap gap-1 border-b border-gray-200 dark:border-gray-700">
        {TABS.map((t) => {
          const Icon = t.icon;
          const activeTab = t.id === tab;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium -mb-px border-b-2 transition-colors ${
                activeTab
                  ? 'border-[var(--accent-strong,#1b3a6b)] text-[var(--accent-link,#2d5295)] dark:text-[var(--accent-link-dark,#93c5fd)]'
                  : 'border-transparent text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white font-medium'
              }`}
            >
              <Icon size={15} /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === 'workspace' && <PayrollWorkspace onOpenStaff={onOpenStaff} />}
      {tab === 'basicpay' && <BasicPayManager onOpenStaff={onOpenStaff} />}
      {tab === 'payrun' && <PayRun embedded />}
      {tab === 'bulk' && <BulkPayslips />}
      {tab === 'reports' && <DeductionReports embedded />}
      {tab === 'bank' && <PayrollBankExport />}
      {tab === 'recon' && <LoanReconciliation />}
      {tab === 'periods' && <PayrollPeriods />}
    </div>
  );
}
